import React, { useRef, useEffect, useState } from "react";
import { FaGithub, FaExternalLinkAlt } from "react-icons/fa";

const Projects = () => {
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    // ✅ Updated projects with direct static paths
    const projects = [
        {
            title: "AI-Powered Disease Prediction System",
            description:
                "Developed an AI-driven disease prediction system using machine learning models. Users input symptoms, and the system predicts possible conditions with confidence scores. Implemented with Python, TensorFlow, and AWS.",
            tags: ["Python", "TensorFlow", "AWS", "ML", "React"],
            github: "https://github.com/abdellah-ouffa",
            video: "/static/AI-Powered Disease Prediction Demo.mp4",
            demo: "https://streamable.com/jpd2lb",
            image: "/static/AI_Powered Disease Prediction System.jpg", // ✅ Corrected path for Django static files
        },
        {
            title: "Smart Home Automation Platform",
            description:
                "Built a comprehensive home automation system with real-time monitoring, custom scheduling, and voice control integration using React, Node.js, and Raspberry Pi.",
            tags: ["React", "Node.js", "MongoDB", "IoT", "Docker"],
            github: "https://github.com/abdellah-ouffa",
            demo: "https://demo-link.com",
            image: "/static/smart_home.jpg", // ✅ Corrected path for Django static files
        },
        {
            title: "3D Flight Simulator",
            description:
                "Developed an interactive 3D flight simulator using Three.js with realistic physics, night mode, and particle effects. Features real-time lighting and WebGL rendering.",
            tags: ["Three.js", "JavaScript", "WebGL", "Game Development"],
            github: "https://github.com/abdellah-ouffa/3d-flight-simulator",
            video: "/static/3D_Flight_Simulator.mp4", // ✅ Corrected path for Django static files
            image: "/static/3D_flight.jpg", // ✅ Corrected path for Django static files
        },
    ];
    return (
        <section
            id="projects"
            ref={sectionRef}
            className={`py-16 bg-gray-100 dark:bg-gray-900 transition-all ${isVisible ? "animate-fadeIn" : "opacity-0"}`}
        >
            <div className="container mx-auto px-6 md:px-12 lg:px-20">
                <h2 className="text-4xl font-extrabold text-gray-800 dark:text-white mb-6 text-center">
                    Featured Projects
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto text-center mb-12">
                    Here are some of my recent projects showcasing my expertise in
                    <span className="font-semibold text-blue-500"> Full-Stack Development</span>,
                    <span className="font-semibold text-green-500"> IoT</span>, and
                    <span className="font-semibold text-purple-500"> AI Integration</span>.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {projects.map((project, index) => (
                        <div
                            key={index}
                            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                        >
                            <div className="relative pb-[56.25%]">
                                {project.video ? (
                                    <video
                                        className="absolute top-0 left-0 w-full h-full object-contain"
                                        controls
                                        poster={project.image}
                                    >
                                        <source src={project.video} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                ) : (
                                    <img
                                        src={project.image}
                                        alt={project.title}
                                        className="absolute top-0 left-0 w-full h-full object-contain"
                                    />
                                )}
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-3">
                                    {project.title}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300 mb-4">
                                    {project.description}
                                </p>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {project.tags.map((tag, tagIndex) => (
                                        <span
                                            key={tagIndex}
                                            className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full text-sm"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <div className="flex justify-between items-center">
                                    <a
                                        href={project.github}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all flex items-center gap-2"
                                    >
                                        <FaGithub className="text-xl" />
                                        Code
                                    </a>
                                    {project.video ? (
                                        <span className="text-gray-500 italic">Video Demo Available</span>
                                    ) : (
                                        <a
                                            href={project.demo}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-500 hover:text-blue-600 transition-all flex items-center gap-2"
                                        >
                                            <FaExternalLinkAlt />
                                            Live Demo
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section >
    );
};

export default Projects;